/**
 * The bar that stays with you: who this is, what is here, and how to leave in
 * either language.
 *
 * Four destinations, a search field and the language switch - no account, no
 * cart, no notification bell, because none of those exist. On a phone the bar
 * keeps the brand, search and language on one line and moves the four
 * destinations into a rail underneath, which is how a game site is browsed on
 * a thumb.
 */

import { useState } from "react";
import { LanguageSelector, useI18n } from "../i18n";
import { BrandMark } from "./BrandMark";
import { PortalSearch } from "./PortalSearch";
import type { PortalSearchProps } from "./PortalSearch";

export type NavTarget = "home" | "games" | "categories" | "learn";

const NAV: readonly NavTarget[] = ["home", "games", "categories", "learn"];

export interface PortalHeaderProps extends Omit<PortalSearchProps, "autoFocus" | "onDismiss"> {
  onNavigate: (target: NavTarget) => void;
  onHome: () => void;
  /** Which destination the current screen counts as, for aria-current. */
  current?: NavTarget;
}

export function PortalHeader({ onNavigate, onHome, current, ...search }: PortalHeaderProps) {
  const { t } = useI18n();
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className="pheader" data-search-open={searchOpen}>
      <div className="pheader__bar">
        <button type="button" className="brandmark" onClick={onHome} aria-label={t("portal.brand.aria")}>
          <BrandMark />
          <span className="brandmark__word">
            kikik<span className="brandmark__tld">.id</span>
          </span>
        </button>

        <nav className="pnav" aria-label={t("portal.nav.label")}>
          {NAV.map((target) => (
            <button
              key={target}
              type="button"
              className="pnav__item"
              data-nav={target}
              aria-current={current === target ? "page" : undefined}
              onClick={() => onNavigate(target)}
            >
              {t(`portal.nav.${target}`)}
            </button>
          ))}
        </nav>

        <div className="pheader__tools">
          <div className="pheader__search">
            <PortalSearch {...search} onDismiss={() => setSearchOpen(false)} />
          </div>
          <button
            type="button"
            className="pheader__searchtoggle"
            aria-expanded={searchOpen}
            aria-label={searchOpen ? t("portal.search.close") : t("portal.search.open")}
            onClick={() => setSearchOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="10.5" cy="10.5" r="6.5" />
              <path d="M15.5 15.5 21 21" />
            </svg>
          </button>
          <LanguageSelector />
        </div>
      </div>

      {searchOpen ? (
        <div className="pheader__drawer">
          <PortalSearch {...search} autoFocus onDismiss={() => setSearchOpen(false)} />
        </div>
      ) : null}
    </header>
  );
}
